import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab } from '@material-ui/core';

import {
  listHinovaCooperativas,
  listHinovaRegionais,
} from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import {
  HinovaCooperativa,
  HinovaRegional,
} from '~/types';
import { Table } from '~/components/Table';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';

enum ListingTab {
  COOPERATIVAS = 0,
  REGIONAIS = 1,
}

export default function Listings() {
  const [activeTab, setActiveTab] = useState<ListingTab>(ListingTab.COOPERATIVAS);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [cooperativas, setCooperativas] = useState<HinovaCooperativa[]>([]);
  const [regionais, setRegionais] = useState<HinovaRegional[]>([]);

  const loadCooperativas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listHinovaCooperativas();
      if (response.status === 200 && response.data) {
        setCooperativas(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível carregar cooperativas.',
      );
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRegionais = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listHinovaRegionais();
      if (response.status === 200 && response.data) {
        setRegionais(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack((error?.applicationErrorMessage || error?.apiError) + ': não foi possível carregar regionais.');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, []);



  useEffect(() => {
    switch (activeTab) {
      case ListingTab.COOPERATIVAS:
        loadCooperativas();
        break;
      case ListingTab.REGIONAIS:
        loadRegionais();
        break;
    }
  }, [activeTab, loadCooperativas, loadRegionais]);


  const renderTable = () => {
    switch (activeTab) {
      case ListingTab.COOPERATIVAS:
        return (
          <Table
            isLoading={loading}
            data={cooperativas}
            columns={{
              codigo_cooperativa: { title: 'Código' },
              nome: { title: 'Nome' },
              cnpj: { title: 'Cnpj/Cpf', render: rowData => rowData.cnpj || rowData.cpf },
              estado: { title: 'Estado', render: rowData => rowData.estado || '-' },
              situacao: { title: 'Situação', render: rowData => rowData.situacao || '-' },
            }}
            localization={{
              body: { emptyDataSourceMessage: 'Nenhuma cooperativa encontrada.' },
              pagination: { labelRowsSelect: 'cooperativas' },
            }}
            options={{ showTitle: false, pageSize: 10, paging: true, filtering: false, search: false }}
          />
        );


      case ListingTab.REGIONAIS:
        return (
          <Table
            isLoading={loading}
            data={regionais}
            columns={{
              codigo_regional: { title: 'Código' },
              nome: { title: 'Nome' },
              estado: { title: 'Estado', render: rowData => rowData.estado || '-' },
              cnpj: { title: 'Cnpj', render: rowData => rowData.cnpj || '-' },
              situacao: { title: 'Situação', render: rowData => rowData.situacao || '-' },
            }}
            localization={{
              body: { emptyDataSourceMessage: 'Nenhuma regional encontrada.' },
              pagination: { labelRowsSelect: 'regionais' },
            }}
            options={{ showTitle: false, pageSize: 10, paging: true, filtering: false, search: false }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
        }}
        indicatorColor='primary'
        textColor='primary'
        style={{ marginBottom: 20 }}
      >
        <Tab label='Cooperativas' />
        <Tab label='Regionais' />
      </Tabs>

      {renderTable()}

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
